import React, { useMemo } from 'react';
import { BasicContainer, TypographyProps, Link, FlexCol, FlexRow, TextDynamic, FlexProps } from '@minecraft.atoms';
import { getEnvironment } from '@minecraft.environment';

type NavItemProps<NavKey extends string> = {
  /** the title renderd on the tab */
  navTitle: string;
  /** the unique key value used to identify the tab, this is the value returned in onTabChange */
  key: NavKey;
  /** the link that the item directs to */
  to: string;
  /** determines whether the link should match the route exactly or if a partial match should be considered active */
  isLinkExact?: boolean;
};

type SubNavLinksSize = 'sm' | 'lg';

export type SubNavLinksProps<NavKey extends string> = {
  /** the list of nav configuration objects, the activeNavItem prop should equal the value of one of the keys */
  navItems: NavItemProps<NavKey>[];
  /** */
  'data-qa-id'?: string;
  /** */
  'data-testid'?: string;
  /** aria-label for this subnav (like what it's the subnav for??) */
  'aria-label': string;
  /** prop to control the size of the nav items & space between, defaults to "lg" */
  size?: SubNavLinksSize;
  /** gap between nav items defaults to 8 (2rem) */
  gap?: FlexProps['gap'];
};

const validateNavItemsProp = <NavKey extends string>({
  navItems,
  componentName,
}: {
  navItems: NavItemProps<NavKey>[];
  componentName: string;
}) => {
  const arr = Array.isArray(navItems) ? navItems : [];
  const keys = [];
  return arr.filter((nav, index) => {
    if (!nav.navTitle) {
      console.warn(`${componentName}: nav with index ${index} is missing navTitle value`);
      return false;
    }

    if (!nav.key) {
      console.warn(`${componentName}: nav with index ${index} is missing key value`);
      return false;
    }

    if (keys.includes(nav.key)) {
      console.warn(`${componentName}: found multiple navItems with key ${nav.key}`);
      return false;
    }

    keys.push(nav.key);
    return true;
  });
};

const SIZE_CONFIGS: Record<SubNavLinksSize, { fontSize: TypographyProps['fontSize'] }> = {
  sm: {
    fontSize: 'detailLarge',
  },
  lg: {
    fontSize: 'bodyRegular',
  },
};

/**
 * SubNavLinks takes a list of objects that describe each nav item to render (what to link it to and how to match it to determine the active route)
 */
export const SubNavLinks = <NavKey extends string>({
  'data-qa-id': dataQaId,
  'data-testid': dataTestId,
  navItems,
  'aria-label': ariaLabel,
  size: sizeProp = 'lg',
  gap = '8',
}: SubNavLinksProps<NavKey>) => {
  const navsTouse = useMemo(() => validateNavItemsProp({ navItems, componentName: 'SubNavLinks' }), [navItems]);
  const currentRoute = window.location.pathname;
  const basePath = getEnvironment().BASE_NAME;
  const activeNavItem = useMemo(() => {
    const pathToSearch = currentRoute.replace(basePath, '');
    return navsTouse.find((nav) => {
      if (nav?.isLinkExact) {
        return pathToSearch === nav?.to;
      }
      return pathToSearch.includes(nav?.to);
    });
  }, [basePath, currentRoute, navsTouse]);

  const size = useMemo(() => {
    if (['sm', 'lg'].includes(sizeProp)) {
      return sizeProp;
    }
    return 'lg';
  }, [sizeProp]);

  return (
    <FlexCol data-qa-id={dataQaId} data-testid={dataTestId}>
      <FlexRow
        maxHeight={3}
        role="navigation"
        className="cn_overflow-x-auto"
        alignItems="flex-end"
        wrap="nowrap"
        gap={gap}
        w="full"
        aria-label={ariaLabel}
      >
        {navsTouse.map((nav) => {
          const isActive = nav?.key === activeNavItem?.key;
          return (
            <Link
              aria-label={nav.navTitle}
              aria-selected={isActive}
              data-qa-id={nav?.['data-qa-id'] || `${nav.key}-tab`}
              disabled={isActive}
              key={nav.key}
              to={nav.to}
            >
              <BasicContainer
                key={nav.key}
                borderPlacement="bottom"
                borderColor={isActive ? 'emphasis' : 'transparent'}
                borderSize={0.09}
                className="cn_atom_py-2"
              >
                <TextDynamic
                  as="p"
                  noWrap
                  fontWeight={isActive ? 'semibold' : 'medium'}
                  color={isActive ? 'default' : 'primary'}
                  fontSize={SIZE_CONFIGS[size].fontSize}
                >
                  {nav.navTitle}
                </TextDynamic>
              </BasicContainer>
            </Link>
          );
        })}
      </FlexRow>
    </FlexCol>
  );
};
